import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { useAuth } from "hooks/useAuth";
import {
  CHAMPION_VIDEO_SLOTS,
  generateChampionVideoShareLink,
  listChampionVideoRenderJobs,
  listChampionVideoShareLinks,
  resolveVideoStatuses,
} from "services/fde/championVideoRolloutService";

const inlineLoading = (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress />
  </Box>
);

const DistributePage = () => {
  const { userData, organizationId, isGlobalAdmin } = useAuth();
  const institutionId =
    userData?.activeInstitutionId || userData?.institutionId || organizationId || null;

  const profileReady = userData !== undefined;
  const canAccess = isGlobalAdmin || userData?.role === "admin";

  const [jobs, setJobs] = useState([]);
  const [linksByVideo, setLinksByVideo] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [copyingId, setCopyingId] = useState("");

  const statuses = useMemo(() => resolveVideoStatuses(jobs), [jobs]);

  const completedSlots = useMemo(
    () =>
      CHAMPION_VIDEO_SLOTS.filter(
        ({ videoId }) => statuses[videoId]?.status === "complete",
      ),
    [statuses],
  );

  const reload = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const [jobRows, linkRows] = await Promise.all([
        listChampionVideoRenderJobs(institutionId),
        listChampionVideoShareLinks(institutionId),
      ]);
      setJobs(jobRows);
      const map = {};
      linkRows.forEach((link) => {
        if (link.videoId) map[link.videoId] = link;
      });
      setLinksByVideo(map);
    } catch (err) {
      setLoadError(true);
      setErrorMessage(err?.message || "Failed to load distribute data.");
      setJobs([]);
      setLinksByVideo({});
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    if (!profileReady || !canAccess || !institutionId) {
      setJobs([]);
      setLinksByVideo({});
      setLoading(false);
      return undefined;
    }
    reload();
    return undefined;
  }, [profileReady, canAccess, institutionId, reload]);

  const absoluteUrl = (path) => {
    if (typeof window === "undefined") return path;
    return `${window.location.origin}${path}`;
  };

  const handleCopy = async (videoId) => {
    setCopyingId(videoId);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      let link = linksByVideo[videoId];
      if (!link?.url) {
        link = await generateChampionVideoShareLink({
          institutionId,
          videoId,
        });
        setLinksByVideo((prev) => ({ ...prev, [videoId]: link }));
      }
      const full = absoluteUrl(link.url);
      await navigator.clipboard.writeText(full);
      setSuccessMessage(`Copied link for ${videoId}.`);
    } catch (err) {
      setErrorMessage(
        err?.message || err?.details || "Could not create or copy share link.",
      );
    } finally {
      setCopyingId("");
    }
  };

  if (!profileReady) return inlineLoading;

  if (!canAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Institution admin access is required.</Alert>
      </Box>
    );
  }

  if (!institutionId) {
    return (
      <Stack spacing={3}>
        <DashboardPageHeader
          title="Distribute"
          subtitle="Shareable links per video"
        />
        <Alert severity="info">
          No institution is linked to your account yet.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <DashboardPageHeader
        title="Distribute"
        subtitle="Shareable links for completed champion rollout videos"
      />

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && !loadError && (
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      {loadError && (
        <DataLoadErrorAlert
          onRetry={reload}
          message={errorMessage || undefined}
        />
      )}

      {loading ? (
        inlineLoading
      ) : completedSlots.length === 0 ? (
        <Alert severity="info">
          No completed rollout videos yet. Request and render videos from the
          Video Library first.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {completedSlots.map(({ videoId, label }) => {
            const link = linksByVideo[videoId];
            return (
              <Box
                key={videoId}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="subtitle1">{label}</Typography>
                    {link?.url ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {absoluteUrl(link.url)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No share link yet
                      </Typography>
                    )}
                    {typeof link?.viewCount === "number" && (
                      <Typography variant="caption" color="text.secondary">
                        Views: {link.viewCount}
                      </Typography>
                    )}
                  </Box>
                  <LoadingButton
                    variant="contained"
                    startIcon={<ContentCopyRounded />}
                    loading={copyingId === videoId}
                    onClick={() => handleCopy(videoId)}
                  >
                    Copy link
                  </LoadingButton>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default DistributePage;
